import { useEffect, useState } from 'react';

import { Main, Logo } from './Layout.js';
import { QuakeList } from './QuakeList.js';
import { CountryList } from './CountryList.js';
import { MagnitudeList } from './MagnitudeList.js';
import { PeriodList } from './PeriodList.js';
import { SortBy } from './SortBy.js';
import { defaultValues } from '../config.js';
import { getQuakeCoordinates } from '../utilities.js';
import Spinner from '../components/ui/Spinner.js';

import Map from './Map.js';
import 'leaflet/dist/leaflet.css';
import { useLocalStorageState } from '../hooks/useLocalStorageState.js';
import Form from './Form.js';
import Sidebar from './Sidebar.js';

export default function App() {
  const [magnitude, setMagnitude] = useLocalStorageState(
    defaultValues.magnitude,
    'magnitude'
  );
  const [period, setPeriod] = useLocalStorageState(
    defaultValues.period,
    'period'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasFilter, setHasFilter] = useState(true);
  const [sortProp, setSortProp] = useLocalStorageState('magnitude', 'sortProp');
  const [quakes, setQuakes] = useState([], 'quakes');

  const [bounds, setBounds] = useLocalStorageState(null, 'bounds');
  const [mapCenter, setMapCenter] = useLocalStorageState(
    [defaultValues.country.latitude, defaultValues.country.longitude],
    'mapCenter'
  );
  const [page, setPage] = useState(1);

  function handleCountryChange(country) {
    setMapCenter([country.latitude, country.longitude], 'mapCenter');
  }

  function handleClickQuake(quake) {
    setMapCenter(getQuakeCoordinates(quake), 'mapCenter');
  }

  function handleSelectPeriod(period) {
    setPeriod(period, 'period');
    setPage(1);
  }

  function handleSelectMagnitude(magnitude) {
    setMagnitude(magnitude, 'magnitude');
    setPage(1);
  }

  useEffect(
    function () {
      async function getQuakes() {
        try {
          setIsLoading(true);
          const res = await fetch(
            `https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/${magnitude}_${period}.geojson`
          );
          if (!res.ok) throw new Error('Problem connecting to USGS feed.');

          const data = await res.json();

          setQuakes(
            data.features.toSorted(
              (a, b) => b.properties.mag - a.properties.mag
            )
          );
          setIsLoading(false);
        } catch (error) {}
      }
      setIsLoading(true);
      getQuakes();
    },
    [magnitude, period]
  );

  return (
    <Main className="main">
      <Sidebar>
        <Logo />
        <p className="disclaimer">
          *Data from <a href="https://earthquake.usgs.gov/">USGS</a> live feed
          <br />
          *Circles represent earthquake significance and not actual extent
        </p>
        <Form>
          <CountryList
            onChange={handleCountryChange}
            mapCenter={mapCenter}
            bounds={bounds}
          />
          <MagnitudeList
            magnitude={magnitude}
            onChange={handleSelectMagnitude}
          />
          <PeriodList period={period} onChange={handleSelectPeriod} />
          <SortBy onChange={setSortProp} />
        </Form>

        {isLoading && <Spinner />}

        {!isLoading && (
          <>
            <div className="hasFilter">
              <input
                type="checkbox"
                name="hasFilter"
                id="hasFilter"
                checked={hasFilter}
                onChange={(e) => {
                  setPage(1);
                  setHasFilter(e.target.checked);
                }}
              />
              <label htmlFor="hasFilter">
                Only list earthquakes shown on the map
              </label>
            </div>
            <QuakeList
              quakes={quakes}
              onSelect={handleClickQuake}
              sortProp={sortProp}
              page={page}
              setPage={setPage}
              bounds={bounds}
              hasFilter={hasFilter}
            />
          </>
        )}
      </Sidebar>

      <Map
        quakes={quakes}
        mapCenter={mapCenter}
        updateBounds={setBounds}
        setPage={setPage}
      />
    </Main>
  );
}
